/**
 * Framework controls hook (v1.1-A3 / Phase B3) — fetches real
 * framework_controls + framework_attestations and adapts each row
 * into the mock UI's Control VM. Drives the controls tab on the
 * /trust/frameworks/$frameworkId detail page.
 *
 * Framework metadata (name, jurisdiction, certificationExpiry,
 * setupProgress, etc.) is NOT backed by A3 and remains mocked. Only
 * the controls list + attestation state come from real data.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api-client';
import type {
  Control,
  ControlFamily,
  ControlStatus,
  ControlMapping,
  FrameworkId,
} from './mock-trust-data';

// ─── server VM (mirror of governance-frameworks.ts) ───────────────

interface ServerAttestation {
  id: string;
  status: 'unattested' | 'attested' | 'attested_with_exception' | 'not_applicable';
  attestedBy: string | null;
  attestedAt: string | null;
  exceptionRationale: string | null;
  expiresAt: string | null;
  evidenceArtifactIds: string[];
  reviewedBy: string | null;
  reviewedAt: string | null;
  notes: string | null;
  updatedAt: string;
  // Trust #12 — control-owner reassignment (migration c15 adds these columns).
  ownerUserId?: string | null;
  ownerName?: string | null;
}

export interface ServerControl {
  id: string;
  frameworkId: string;
  controlCode: string;
  category: string | null;
  title: string;
  description: string | null;
  guidance: string | null;
  parentCode: string | null;
  displayOrder: number;
  requiredEvidenceTypes: string[];
  attestation: ServerAttestation | null;
}

interface ListResponse {
  frameworkId: string;
  controls: ServerControl[];
}

// ─── id maps ──────────────────────────────────────────────────────

// Mock UI uses underscored framework ids; A3 uses hyphenated.
export const UI_TO_SERVER_FRAMEWORK_ID: Partial<Record<FrameworkId, string>> = {
  itar: 'itar',
  cmmc_l2: 'cmmc-l2',
  hipaa: 'hipaa',
  gdpr: 'gdpr',
  // mock has no 'eu-ai-act' / 'nist-ai-rmf' / 'osha' / 'sox' direct match;
  // the iso42001 / nist_ai_rmf / iso27001 / soc2 entries in the mock
  // are the closest stand-ins where applicable.
  iso42001: 'eu-ai-act',
  nist_ai_rmf: 'nist-ai-rmf',
  iso27001: 'osha',
  soc2: 'sox',
};

// Heuristic mapping of a category string (e.g. "Access Control") to a
// ControlFamily enum. Best-effort; unknown categories collapse to
// 'system_information_integrity'.
function categoryToFamily(category: string | null): ControlFamily {
  const c = (category ?? '').toLowerCase();
  if (c.includes('access')) return 'access_control';
  if (c.includes('identif') || c.includes('authent')) return 'identification_authentication';
  if (c.includes('audit')) return 'audit_accountability';
  if (c.includes('config')) return 'configuration_management';
  if (c.includes('incident')) return 'incident_response';
  if (c.includes('maint')) return 'maintenance';
  if (c.includes('media')) return 'media_protection';
  if (c.includes('personnel') || c.includes('people')) return 'personnel_security';
  if (c.includes('physical')) return 'physical_protection';
  if (c.includes('risk')) return 'risk_assessment';
  if (c.includes('assessment') || c.includes('security_assessment'))
    return 'security_assessment';
  if (c.includes('comm') || c.includes('transmission') || c.includes('communications'))
    return 'system_communications';
  if (c.includes('aware') || c.includes('train')) return 'awareness_training';
  if (c.includes('data')) return 'data_governance';
  if (c.includes('ai') || c.includes('govern')) return 'ai_governance';
  return 'system_information_integrity';
}

function attestationToStatus(a: ServerAttestation | null): ControlStatus {
  if (!a || a.status === 'unattested') return 'not_implemented';
  if (a.status === 'attested') return 'passing';
  if (a.status === 'attested_with_exception') return 'partial';
  if (a.status === 'not_applicable') return 'not_applicable';
  return 'not_implemented';
}

export function adaptServerControl(
  c: ServerControl,
  uiFrameworkId: FrameworkId,
): Control {
  const a = c.attestation;
  const mapping: ControlMapping = {
    framework: uiFrameworkId,
    controlId: c.controlCode,
    controlName: c.title,
    relevance: 'primary',
  };
  return {
    id: c.id,
    name: c.title,
    description: c.description ?? '',
    family: categoryToFamily(c.category),
    status: attestationToStatus(a),
    automation: 'manual',
    priority: 'medium',
    // c15: control owner now persists on the attestation row (Trust bug #12).
    // Fall back to the attester, then an em-dash placeholder.
    ownerUserId: a?.ownerUserId ?? a?.attestedBy ?? '',
    ownerName: a?.ownerName ?? '—',
    mappings: [mapping],
    evidenceIds: a?.evidenceArtifactIds ?? [],
    lastTestedAt: a?.attestedAt ?? null,
    testFrequency: 'quarterly',
    nextReviewAt: a?.expiresAt ?? new Date(Date.now() + 90 * 86400000).toISOString(),
    failureCount30d: 0,
    passCount30d: a?.status === 'attested' ? 1 : 0,
    remediationDeadline: null,
    notes: a?.notes ?? c.guidance ?? '',
    argusAiPrimitive: null,
    testProcedure: c.guidance ?? '—',
    expectedResult: '—',
    lastResult: a?.exceptionRationale ?? null,
  };
}

// ─── Hook ─────────────────────────────────────────────────────────

// (above this) added useSetControlStatus(): maps UI status → attestation status
// (passing→attested, partial→attested_with_exception, na→not_applicable,
// failing/needs_review/not_implemented→unattested) and PUTs .../attestation +
// invalidate. See file.
export function useFrameworkControls(uiFrameworkId: FrameworkId | undefined) {
  const serverId = uiFrameworkId
    ? UI_TO_SERVER_FRAMEWORK_ID[uiFrameworkId]
    : undefined;
  return useQuery<ListResponse, Error, Control[]>({
    queryKey: ['trust', 'framework-controls', serverId],
    enabled: !!serverId && !!uiFrameworkId,
    queryFn: () =>
      apiClient.get<ListResponse>(`/governance/frameworks/${serverId}/controls`),
    select: (res) =>
      res.controls.map((c) => adaptServerControl(c, uiFrameworkId!)),
  });
}

function serverFrameworkId(uiFrameworkId: FrameworkId | undefined): string | undefined {
  return uiFrameworkId ? UI_TO_SERVER_FRAMEWORK_ID[uiFrameworkId] : undefined;
}

// Trust #12 — reassign a control's owner (PUT .../controls/:code/owner).
export function useReassignControlOwner(uiFrameworkId: FrameworkId | undefined) {
  const qc = useQueryClient();
  const serverId = serverFrameworkId(uiFrameworkId);
  return useMutation<
    unknown,
    Error,
    { controlCode: string; ownerUserId?: string | null; ownerName: string }
  >({
    mutationFn: ({ controlCode, ownerUserId, ownerName }) =>
      apiClient.put(
        `/governance/frameworks/${serverId}/controls/${encodeURIComponent(controlCode)}/owner`,
        { ownerUserId: ownerUserId ?? null, ownerName },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['trust', 'framework-controls', serverId] });
    },
  });
}

// Trust #13 — set a control's status via its attestation. Maps the UI status
// vocabulary onto the attestation status the backend persists.
const UI_STATUS_TO_ATTESTATION: Record<string, string> = {
  passing: 'attested',
  partial: 'attested_with_exception',
  na: 'not_applicable',
  not_applicable: 'not_applicable',
  failing: 'unattested',
  needs_review: 'unattested',
  not_implemented: 'unattested',
};
export function useSetControlStatus(uiFrameworkId: FrameworkId | undefined) {
  const qc = useQueryClient();
  const serverId = serverFrameworkId(uiFrameworkId);
  return useMutation<unknown, Error, { controlCode: string; status: string; rationale?: string }>({
    mutationFn: ({ controlCode, status, rationale }) => {
      const attStatus = UI_STATUS_TO_ATTESTATION[status] ?? 'unattested';
      return apiClient.put(
        `/governance/frameworks/${serverId}/controls/${encodeURIComponent(controlCode)}/attestation`,
        {
          status: attStatus,
          exceptionRationale: attStatus === 'attested_with_exception' ? (rationale ?? 'Marked via Trust UI') : null,
        },
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['trust', 'framework-controls', serverId] });
    },
  });
}
